import { useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  LoadingState,
  PageHeader,
  StatusPill,
} from '@/components/ui';
import type {
  AdminDesignColor,
  AdminDesignMockup,
  AdminDesignProduct,
} from '@/api/admin';
import { useAdminDesign } from '@/hooks/useAdminDesigns';
import { getApiErrorCode } from '@/lib/api-errors';
import {
  DESIGN_STATUS_LABEL,
  DESIGN_STATUS_VARIANT,
  MOCKUP_STATUS_LABEL,
  MOCKUP_STATUS_VARIANT,
  formatAdminDate,
} from './admin-design-labels';

function isNotFound(error: unknown): boolean {
  return (
    getApiErrorCode(error) === 'design_not_found' ||
    (axios.isAxiosError(error) && error.response?.status === 404)
  );
}

/** List search handed over by the list page, so "back" keeps its filters. */
function useListHref(): string {
  const { state } = useLocation();
  const listSearch =
    typeof state === 'object' &&
    state !== null &&
    'listSearch' in state &&
    typeof state.listSearch === 'string'
      ? state.listSearch
      : '';
  return listSearch ? `/admin/designs?${listSearch}` : '/admin/designs';
}

function Thumbnail({ src, alt }: { src: string | null; alt: string }) {
  return (
    <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-control bg-paper ring-1 ring-line">
      {src ? (
        <img src={src} alt={alt} className="h-full w-full object-contain" />
      ) : (
        <span className="text-xs text-mute">No image</span>
      )}
    </div>
  );
}

function MockupTile({
  mockup,
  colorName,
}: {
  mockup: AdminDesignMockup;
  colorName: string;
}) {
  return (
    <li className="flex w-28 flex-col gap-1.5" data-testid="admin-mockup">
      <Thumbnail
        src={mockup.status === 'COMPLETED' ? mockup.imageUrl : null}
        alt={`${colorName} ${mockup.positionSlug}`}
      />
      <span className="truncate font-mono text-xs text-ink-2">
        {mockup.positionSlug}
      </span>
      <StatusPill variant={MOCKUP_STATUS_VARIANT[mockup.status]}>
        {MOCKUP_STATUS_LABEL[mockup.status]}
      </StatusPill>
      {mockup.status === 'FAILED' && mockup.errorMessage && (
        <p className="break-words text-xs text-danger">{mockup.errorMessage}</p>
      )}
    </li>
  );
}

function ColorRow({ color }: { color: AdminDesignColor }) {
  return (
    <li className="flex flex-col gap-3 border-t border-line pt-4">
      <div className="flex items-center gap-2">
        <span
          className="h-4 w-4 rounded-full ring-1 ring-line"
          style={{ backgroundColor: color.hexCode }}
          aria-hidden="true"
        />
        <h4 className="text-sm font-semibold text-ink">{color.name}</h4>
        <span className="font-mono text-xs text-mute">{color.hexCode}</span>
      </div>
      {color.mockups.length > 0 ? (
        <ul className="flex flex-wrap gap-4">
          {color.mockups.map((mockup) => (
            <MockupTile
              key={mockup.positionSlug}
              mockup={mockup}
              colorName={color.name}
            />
          ))}
        </ul>
      ) : (
        <p className="text-xs text-mute">No mockups for this color.</p>
      )}
    </li>
  );
}

function ProductSection({ product }: { product: AdminDesignProduct }) {
  return (
    <Card
      className="flex flex-col gap-4"
      data-testid={`admin-design-product-${product.id}`}
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="dc-h3 text-base text-ink">{product.productName}</h2>
          <p className="font-mono text-xs text-mute">
            {product.artNumber ?? '—'}
          </p>
        </div>
        <StatusPill variant={product.isEnabled ? 'published' : 'archived'}>
          {product.isEnabled ? 'Enabled' : 'Disabled'}
        </StatusPill>
      </header>

      <div className="flex flex-col gap-2">
        <h3 className="dc-eyebrow">Positions</h3>
        {product.positions.length > 0 ? (
          <ul className="flex flex-wrap gap-4">
            {product.positions.map((position) => (
              <li key={position.slug} className="flex w-28 flex-col gap-1.5">
                <Thumbnail
                  src={position.designCropUrl}
                  alt={`Crop ${position.slug}`}
                />
                <span className="truncate font-mono text-xs text-ink-2">
                  {position.slug}
                </span>
                {position.isCustomPlacement && (
                  <StatusPill variant="neutral">Custom placement</StatusPill>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-mute">No positions.</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="dc-eyebrow">Colors</h3>
        {product.colors.length > 0 ? (
          <ul className="flex flex-col gap-4">
            {product.colors.map((color) => (
              <ColorRow key={color.productColorId} color={color} />
            ))}
          </ul>
        ) : (
          <p className="text-xs text-mute">No colors.</p>
        )}
      </div>
    </Card>
  );
}

/**
 * Admin: one design of any account for reviewing an import — owner,
 * products with positions and crop images, colors with their mockups and
 * the error message of every failed render. Read-only.
 */
export function AdminDesignDetailPage() {
  const { designId } = useParams<{ designId: string }>();
  const listHref = useListHref();
  const {
    data: design,
    isLoading,
    isError,
    error,
    refetch,
  } = useAdminDesign(designId);

  useEffect(() => {
    document.title = 'Design Admin – DesignDeck';
    return () => {
      document.title = 'DesignDeck';
    };
  }, []);

  const backLink = (
    <Button variant="outline" asChild>
      <Link to={listHref}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to designs
      </Link>
    </Button>
  );

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <LoadingState label="Loading design…" />
      </div>
    );
  }

  if (isError && isNotFound(error)) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <EmptyState
          title="Design not found"
          description="The design does not exist or has been deleted."
          action={backLink}
        />
      </div>
    );
  }

  if (isError || !design) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <Card className="flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-danger">Failed to load the design.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
            {backLink}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <PageHeader
        eyebrow="Admin · Design"
        title={design.name}
        description={
          <span className="font-mono">
            {design.sku} · {design.products.length} products
          </span>
        }
        action={backLink}
      />

      <Card
        padding="sm"
        className="flex flex-wrap items-center gap-x-6 gap-y-2"
      >
        <StatusPill variant={DESIGN_STATUS_VARIANT[design.status]}>
          {DESIGN_STATUS_LABEL[design.status]}
        </StatusPill>
        <span className="text-sm text-ink-2">
          Owner{' '}
          <Link
            to={`/admin/users?search=${encodeURIComponent(design.owner.email)}`}
            className="text-ink underline-offset-2 hover:underline"
          >
            {design.owner.email}
          </Link>
        </span>
        <span className="font-mono text-xs text-mute">
          {design.owner.shirteeDesignerId !== null
            ? `Designer ${design.owner.shirteeDesignerId}`
            : 'No designer id'}
        </span>
        <span className="font-mono text-xs text-mute">
          Source {design.createdVia ?? '—'}
        </span>
        <span className="font-mono text-xs text-mute">
          Published {formatAdminDate(design.publishedAt)}
        </span>
      </Card>

      {design.products.length > 0 ? (
        design.products.map((product) => (
          <ProductSection key={product.id} product={product} />
        ))
      ) : (
        <EmptyState
          title="No products"
          description="This design has no products assigned."
        />
      )}
    </div>
  );
}
